'use client'

import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'
import type { Socket } from 'socket.io-client'
import { useAuth } from '@/features/auth'
import { announceIdentity, createSocket } from './socket-client'

interface SocketContextValue {
  /** The shared connection, or null when realtime is disabled (no url). */
  socket: Socket | null
}

const SocketContext = createContext<SocketContextValue>({ socket: null })

interface SocketProviderProps {
  children: ReactNode
  /**
   * Realtime server origin (the Go server's Socket.IO endpoint), resolved
   * server-side from the active environment's `websocketUrl` and passed down —
   * `environment-selector` is server-only, so a client component can't read it
   * directly. Empty string disables realtime (SSR / tests without a socket).
   */
  url: string
}

/**
 * Owns the app's single Socket.IO connection and re-announces identity on every
 * (re)connect. Mounted once, high in the tree (inside AuthProvider so it can read
 * the signed-in user, and inside QueryClientProvider so subscriber hooks can
 * invalidate). Consumers reach the socket via {@link useSocket}; the room-scoped
 * liveness hooks ({@link useHiveLiveness}, {@link usePostLiveness}) build on it.
 */
export function SocketProvider({ children, url }: SocketProviderProps) {
  const { user } = useAuth()
  const userId = user?.id
  const socketRef = useRef<Socket | null>(null)

  // Create the socket once per url. Re-announce identity whenever the connection
  // (re)establishes — the grace-mode handshake is `io_connect` + the User id.
  const socket = useMemo(() => {
    if (!url) return null
    const s = createSocket(url)
    socketRef.current = s
    return s
  }, [url])

  useEffect(() => {
    if (!socket) return
    const onConnect = () => announceIdentity(socket, userId)
    socket.on('connect', onConnect)
    socket.connect()
    return () => {
      socket.off('connect', onConnect)
      socket.disconnect()
    }
    // socket is stable per url; userId is read fresh inside onConnect via closure,
    // but we re-run if it changes so a login mid-session re-announces.
  }, [socket, userId])

  const value = useMemo<SocketContextValue>(() => ({ socket }), [socket])

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

/** The shared socket, or null when realtime is disabled. */
export function useSocket(): Socket | null {
  return useContext(SocketContext).socket
}
